import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { FocusContext, TFocusableElement } from 'src/contexts/FocusContext';
import { sortFocusableElements } from './FocusProvider.utils';
import { logFocusInfo, logFocusWarning } from 'src/_shared/logger';

type TFocusableElementsMap = {
  // id, must be unique
  [id: TFocusableElement['id']]: TFocusableElement;
};

export const FocusProvider = ({ children }: PropsWithChildren) => {
  const focusableElementsRefs = useRef<TFocusableElementsMap>({});
  const currentFocusableElementIdRef = useRef<TFocusableElement['id'] | null>(null);
  const currentFocusableGroupsRef = useRef<TFocusableElement['group'][] | null>(null);
  const [currentFocusableElementId, _setCurrentFocusableElementId] = useState<TFocusableElement['id'] | null>(null);
  const setCurrentFocusableElementId = (id: TFocusableElement['id'] | null) => {
    currentFocusableElementIdRef.current = id;
    _setCurrentFocusableElementId(id);
  };

  const addFocusableElement = ({ id, ref, order, group, focusable = true }: TFocusableElement) => {
    focusableElementsRefs.current[id] = {
      id,
      ref,
      order,
      group,
      focusable,
    };
  };

  const removeFocusableElement = (refID: TFocusableElement['id']) => {
    if (focusableElementsRefs.current[refID]) {
      delete focusableElementsRefs.current[refID];
    }
  };

  const changeFocusableElementProps = (changedProps: Partial<TFocusableElement>) => {
    if (changedProps.id && focusableElementsRefs.current[changedProps.id]) {
      focusableElementsRefs.current[changedProps.id] = {
        ...focusableElementsRefs.current[changedProps.id],
        ...changedProps,
      };
    }
  };

  const focusCloseFocusableElement = (needToFocusNextElement: boolean) => {
    const sortedFocusableElements = sortFocusableElements(
      currentFocusableGroupsRef.current,
      Object.values(focusableElementsRefs.current),
      needToFocusNextElement,
    );

    if (sortedFocusableElements.length > 0) {
      const currentFocusableElement =
        typeof currentFocusableElementIdRef.current === 'string'
          ? focusableElementsRefs.current[currentFocusableElementIdRef.current]
          : null;

      const currentFocusableElementIndex = currentFocusableElement?.id
        ? sortedFocusableElements.findIndex(({ id }) => id === currentFocusableElement.id)
        : -1;

      const nextFocusableElement = sortedFocusableElements[currentFocusableElementIndex + 1];
      if (nextFocusableElement?.id) {
        focusElement(nextFocusableElement.id);
      } else if (sortedFocusableElements[0]?.id) {
        focusElement(sortedFocusableElements[0].id);
      }
    }
  };

  const pressCurrentFocusableElement = () => {
    if (currentFocusableElementIdRef.current) {
      focusableElementsRefs.current[currentFocusableElementIdRef.current]?.ref?.current?.click();
    }
  };

  const focusElement = (id: TFocusableElement['id']) => {
    const focusableElement = focusableElementsRefs.current[id];
    if (id !== currentFocusableElementIdRef.current && focusableElement?.ref?.current) {
      const currentFocusableElement =
        currentFocusableElementIdRef.current && focusableElementsRefs.current[currentFocusableElementIdRef.current];
      logFocusInfo(
        `Focusing [${focusableElement.group}-${focusableElement.order}]<${id}>`,
        currentFocusableElement &&
          ` & Bluring [${currentFocusableElement.group}-${currentFocusableElement.order}]<${currentFocusableElementIdRef.current}>`,
      );
      if (
        currentFocusableElement &&
        currentFocusableElement.group === focusableElement.group &&
        currentFocusableElement.order === focusableElement.order
      ) {
        logFocusWarning(
          'Duplicate focus',
          `Current [${focusableElement.group}-${focusableElement.order}]<${id}>`,
          currentFocusableElement &&
            ` & Previous [${currentFocusableElement.group}-${currentFocusableElement.order}]<${currentFocusableElementIdRef.current}>`,
        );
      }
      if (currentFocusableElementIdRef.current) {
        blurElement(currentFocusableElementIdRef.current);
      }
      setCurrentFocusableElementId(id);
      focusableElement.ref.current.focus();
    }
  };

  const blurElement = (id: TFocusableElement['id']) => {
    const focusableElement = focusableElementsRefs.current[id];
    if (focusableElement?.ref?.current) {
      setCurrentFocusableElementId(null);
      focusableElement.ref.current.blur();
    }
  };

  const isFocused = (id: TFocusableElement['id']) => {
    return currentFocusableElementId === id;
  };

  const makeOnlySomeElementGroupsFocusable = (groups: TFocusableElement['group'][]) => {
    logFocusInfo(`Making group ${groups} focusable`);
    currentFocusableGroupsRef.current = groups;
  };

  const makeAllElementGroupsFocusable = () => {
    logFocusInfo('Making all groups focusable');
    currentFocusableGroupsRef.current = null;
  };

  useEffect(() => {
    const keyDownHandler = event => {
      // console.log('@@@ keydown code', event.code);
      switch (event.code) {
        case 'Tab':
          event.preventDefault();
          event.stopPropagation();
          focusCloseFocusableElement(!event.shiftKey);
          break;
        case 'Enter':
        case 'Space':
          if (currentFocusableElementIdRef.current) {
            event.preventDefault();
            event.stopPropagation();
            pressCurrentFocusableElement();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', keyDownHandler);
    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  return (
    <FocusContext.Provider
      value={{
        addFocusableElement,
        removeFocusableElement,
        focusElement,
        blurElement,
        isFocused,
        makeOnlySomeElementGroupsFocusable,
        makeAllElementGroupsFocusable,
        changeFocusableElementProps,
        focusCloseFocusableElement,
      }}
    >
      {children}
    </FocusContext.Provider>
  );
};
